/**
 * Design System — centralized, token-driven primitives.
 * Components consume CSS variables set by ThemeProvider. NO hardcoded colors here.
 */
import React from "react";

const cx = (...c) => c.filter(Boolean).join(" ");

// Mar 2026 — Spinner keyframes injected once (no external CSS dependency).
if (typeof document !== "undefined" && !document.getElementById("ds-spinner-kf")) {
  const s = document.createElement("style");
  s.id = "ds-spinner-kf";
  s.textContent = "@keyframes ds-spin { to { transform: rotate(360deg); } }";
  document.head.appendChild(s);
}

// ============ Button ============
// Mar 2026 — Global button pending / double-click protection.
// Any caller may pass `loading` (or its alias `pending`) to signal an in-flight
// async action. When `loading` is truthy the button is FORCE-disabled,
// prepends a spinner, sets `aria-busy=true` and keeps its label to prevent
// layout shift. Callers keep their own `disabled` prop for validation-based
// disabling (e.g. `disabled={!canSubmit}`) — the two flags are combined.
export function Button({
  variant = "primary", size = "md", full = false,
  loading = false, pending = false,
  loadingLabel,
  children, className = "", disabled, ...props
}) {
  const isPending = !!(loading || pending);
  const isDisabled = !!(disabled || isPending);
  return (
    <button
      className={cx("ds-btn", `ds-btn--${variant}`, `ds-btn--${size}`,
                    full && "ds-btn--full",
                    isPending && "ds-btn--loading", className)}
      disabled={isDisabled}
      aria-busy={isPending || undefined}
      data-loading={isPending || undefined}
      {...props}
    >
      {isPending && (
        <span aria-hidden="true"
              style={{
                display: "inline-block", width: "0.9em", height: "0.9em",
                marginRight: 6, border: "2px solid currentColor",
                borderRightColor: "transparent", borderRadius: "50%",
                verticalAlign: "-2px",
                animation: "ds-spin 0.7s linear infinite",
              }} />
      )}
      {isPending && loadingLabel ? loadingLabel : children}
    </button>
  );
}

// ============ Card ============
export function Card({ children, className = "", tone = "default", ...props }) {
  return (
    <div className={cx("ds-card", `ds-card--${tone}`, className)} {...props}>
      {children}
    </div>
  );
}
Card.Header = function CardHeader({ children, className = "" }) {
  return <div className={cx("ds-card__header", className)}>{children}</div>;
};
Card.Body = function CardBody({ children, className = "" }) {
  return <div className={cx("ds-card__body", className)}>{children}</div>;
};
Card.Footer = function CardFooter({ children, className = "" }) {
  return <div className={cx("ds-card__footer", className)}>{children}</div>;
};

// ============ Input & Label ============
export function Label({ children, htmlFor, className = "" }) {
  return <label htmlFor={htmlFor} className={cx("ds-label", className)}>{children}</label>;
}
export function Input({ className = "", ...props }) {
  return <input className={cx("ds-input", className)} {...props} />;
}

// ============ Badge / Status indicator ============
export function Badge({ tone = "neutral", children, ...rest }) {
  return <span className={cx("ds-badge", `ds-badge--${tone}`)} {...rest}>{children}</span>;
}

// ============ Alert ============
export function Alert({ tone = "info", title, children, ...rest }) {
  return (
    <div className={cx("ds-alert", `ds-alert--${tone}`)} role="alert" {...rest}>
      {title && <div className="ds-alert__title">{title}</div>}
      <div className="ds-alert__body">{children}</div>
    </div>
  );
}

// ============ Table ============
export function Table({ columns, rows, emptyText = "Nessun dato" }) {
  return (
    <div className="ds-table-wrap">
      <table className="ds-table">
        <thead>
          <tr>{columns.map((c) => <th key={c.key}>{c.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr><td colSpan={columns.length} className="ds-table__empty">{emptyText}</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={r.id ?? i}>
              {columns.map((c) => <td key={c.key}>{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============ Page section ============
export function Section({ title, subtitle, actions, children, ...rest }) {
  return (
    <section className="ds-section" {...rest}>
      {(title || actions) && (
        <header className="ds-section__head">
          <div>
            {title && <h2 className="ds-section__title">{title}</h2>}
            {subtitle && <p className="ds-section__subtitle">{subtitle}</p>}
          </div>
          {actions && <div className="ds-section__actions">{actions}</div>}
        </header>
      )}
      <div className="ds-section__body">{children}</div>
    </section>
  );
}
